import axiosInstance from '../../../services/axiosConfig';

// ✨ RMMS API Base URL - axiosInstance already has the base URL configured

// ✨ Company ID will be from sessionStorage (TEMPCOM)
const getCompanyId = () => {
  // Get from sessionStorage (set by TesisSec) - stored as JSON string
  const sessionCompanyId = sessionStorage.getItem('TEMPCOM');

  if (sessionCompanyId) {
    try {
      return parseInt(JSON.parse(sessionCompanyId));
    } catch (e) {
      // Fallback if not JSON encoded
      return parseInt(sessionCompanyId);
    }
  }
  // Fallback to localStorage
  const localCompanyId = localStorage.getItem('temp_com_id');
  if (localCompanyId) {
    return parseInt(localCompanyId);
  }
  // No default - companyId must be set via TesisSec
  return null;
};

// Removed localStorage fallback functionality - using API only

const ensureId = (page) => ({
  ...page,
  id: page?.id ?? Date.now(),
  createdAt: page?.createdAt || new Date().toISOString(),
  updatedAt: new Date().toISOString(),
});

const defaultProps = (type) => {
  switch (type) {
    case 'text-label': return { text: 'Label', fontSize: 14, fontWeight: 400 };
    case 'text-box': return { placeholder: 'Text...', fontSize: 14 };
    case 'button': return { text: 'Button', action: 'none' };
    case 'led-indicator': return { state: 'inactive', color: '#4caf50' };
    case 'progress-bar': return { min: 0, max: 100, value: 40 };
    case 'gauge': return { value: 0, unit: '' };
    case 'trend-chart': return { title: 'Trend', tagIds: [] };
    case 'alarm-list': return { limit: 50 };
    default: return {};
  }
};

const inferType = (w) => {
  if (w?.type) {
    const type = String(w.type);
    // DynPage type normalization - keep as lowercase
    const dynPageTypeMap = {
      'Popup': 'popup',
      'Text': 'text',
      'text': 'text',
      'button': 'button',
      'Button': 'button',
      'Frame': 'frame',
      'frame': 'frame',
      'On/Off': 'on/off',
      'on/off': 'on/off',
      'Tag': 'tag',
      'tag': 'tag',
      'SelectBox': 'selectbox',
      'selectbox': 'selectbox',
      'Set': 'text-box',  // DynPage Set widget (input field for setting values)
      'set': 'text-box',
    };
    return dynPageTypeMap[type] || type.toLowerCase();
  }
  if (w?.component) {
    const m = {
      TextLabel: 'text-label',
      TextBox: 'text-box',
      Button: 'button',
      LEDIndicator: 'led-indicator',
      ProgressBar: 'progress-bar',
      Gauge: 'gauge',
      TrendChart: 'trend-chart',
      AlarmList: 'alarm-list',
      PopupWidget: 'popup',
      Box: 'frame',
      ToggleSwitch: 'on/off',
      ComboBox: 'selectbox'
    };
    return m[w.component] || String(w.component).toLowerCase();
  }
  if (w?.id && typeof w.id === 'string') {
    const parts = w.id.split('-');
    if (parts.length > 2) return parts.slice(0, parts.length - 1).join('-');
    if (parts.length > 0) return parts[0];
  }
  return 'widget';
};

const typeToComponent = {
  'text-label': 'TextLabel',
  'text-box': 'TextBox',
  'button': 'Button',
  'led-indicator': 'LEDIndicator',
  'progress-bar': 'ProgressBar',
  'gauge': 'Gauge',
  'trend-chart': 'TrendChart',
  'alarm-list': 'AlarmList',
  'popup': 'PopupWidget',      // DynPage Popup support
  'popup-widget': 'PopupWidget', // Alternative popup format
  'text': 'TextLabel',          // DynPage text widget
  'frame': 'Box',               // DynPage frame widget
  'on/off': 'ToggleSwitch',     // DynPage on/off widget
  'tag': 'TextLabel',           // DynPage tag widget (displays tag value)
  'selectbox': 'ButtonMenu',    // DynPage selectbox widget → ButtonMenu for better UX
};

const normalizeWidgets = (widgets) => {
  if (!Array.isArray(widgets)) return [];
  const baseX = 50, baseY = 50, step = 30;
  const normalized = [];

  for (let i = 0; i < widgets.length; i++) {
    try {
      const w = widgets[i];
      let type = inferType(w);

      // Support DynPage format: elementDetails -> props
      // Merge w.properties and w.props to avoid losing properties
      let rawProps = {
        ...defaultProps(type),
        ...(w.elementDetails || {}),
        ...(w.properties || {}),
        ...(w.props || {})
      };

      // 🔧 SELECTBOX CONVERSION FIX:
      // If selectbox is read-only status display (has tagId, 2 options like ALARM/NORMAL),
      // convert it to text-label with conditional formatting
      if (type === 'selectbox' && rawProps) {
        const hasTagId = rawProps.tagId || rawProps.tag || w.tagId || w.tag;
        const options = rawProps.options || rawProps.values || [];

        // Check if it's a status display (2 options, typically ALARM/NORMAL)
        if (hasTagId && Array.isArray(options) && options.length === 2) {
          // Convert to text-label
          type = 'text-label';

          // Create conditional formatting based on options
          // Options should have: {label, value, color, textColor}
          // We'll use the first option as default (usually NORMAL=0)
          const defaultOption = options[0];
          rawProps.text = defaultOption.label || 'UNKNOWN';
          rawProps.backgroundColor = defaultOption.color || '#ffffff';
          rawProps.foreColor = defaultOption.textColor || '#000000';

          // Store options for potential future use
          rawProps.originalOptions = options;
          rawProps.isStatusLabel = true;
        }
      }

      // DynPage property name mapping to AxisScada format
      const mappedProps = { ...rawProps };

      // Always map tag properties (from rawProps and top-level)
      if (rawProps.tag !== undefined) mappedProps.tagId = rawProps.tag;
      if (rawProps.tagId !== undefined) mappedProps.tagId = rawProps.tagId;
      if (w.tag !== undefined) mappedProps.tagId = w.tag;
      if (w.tagId !== undefined) mappedProps.tagId = w.tagId;

      // Handle tagBinding format (from converter): {tagId: "123", property: "value"}
      if (rawProps.tagBinding?.tagId) {
        mappedProps.tagId = rawProps.tagBinding.tagId;
        mappedProps.tagBinding = rawProps.tagBinding; // Keep tagBinding for reference
      }
      if (w.properties?.tagBinding?.tagId) {
        mappedProps.tagId = w.properties.tagBinding.tagId;
        mappedProps.tagBinding = w.properties.tagBinding; // Keep tagBinding for reference
      }

      // ALWAYS map style properties (not just when elementDetails exists)
      // Map DynPage property names to AxisScada property names
      if (rawProps.bgColor) mappedProps.backgroundColor = rawProps.bgColor;
      if (rawProps.fontColor) mappedProps.foreColor = rawProps.fontColor;
      if (rawProps.borderColor) mappedProps.borderColor = rawProps.borderColor;
      if (rawProps.borderWidth !== undefined) mappedProps.borderWidth = rawProps.borderWidth;
      if (rawProps.borderRadius !== undefined) mappedProps.borderRadius = rawProps.borderRadius;
      if (rawProps.cornerRadius !== undefined) mappedProps.borderRadius = rawProps.cornerRadius; // DynPage uses cornerRadius

      if (w.elementDetails || w.bgColor || w.fontColor) {
        // Map additional DynPage property names
        if (rawProps.fontSize) mappedProps.fontSize = rawProps.fontSize;
        if (rawProps.fontWeight) mappedProps.fontWeight = rawProps.fontWeight;
        if (rawProps.fontFamily) mappedProps.fontFamily = rawProps.fontFamily;
        if (rawProps.textAlign) mappedProps.textAlign = rawProps.textAlign;
        if (rawProps.opacity !== undefined) mappedProps.opacity = rawProps.opacity;
        if (rawProps.value) mappedProps.value = rawProps.value;
        if (rawProps.unit) mappedProps.unit = rawProps.unit;

      }

      // 🔧 SELECTBOX/BUTTONMENU: Map options/values for ButtonMenu
      // DynPage may use 'values' instead of 'options'
      if (type === 'selectbox' && !mappedProps.options) {
        const optionsSource = rawProps.options || rawProps.values || w.options || w.values;
        if (Array.isArray(optionsSource) && optionsSource.length > 0) {
          // Normalize options to {label, value} format
          mappedProps.options = optionsSource.map(opt => {
            // Handle different DynPage option formats
            if (typeof opt === 'string') {
              return { label: opt, value: opt };
            } else if (opt && typeof opt === 'object') {
              return {
                label: opt.label || opt.text || opt.name || String(opt.value),
                value: opt.value !== undefined ? opt.value : (opt.id !== undefined ? opt.id : opt.label || opt.text)
              };
            }
            return { label: String(opt), value: opt };
          });
          console.log(`[pageService] Mapped options for selectbox:`, mappedProps.options);
        }
      }

      // ALWAYS map text property (not just when elementDetails exists)
      if (rawProps.text) mappedProps.text = rawProps.text;

      // ALWAYS map top-level widget properties (not just when elementDetails exists)
      if (w.bgColor) mappedProps.backgroundColor = w.bgColor;
      if (w.fontColor) mappedProps.foreColor = w.fontColor;
      if (w.borderColor) mappedProps.borderColor = w.borderColor;
      if (w.borderWidth !== undefined) mappedProps.borderWidth = w.borderWidth;
      if (w.borderRadius !== undefined) mappedProps.borderRadius = w.borderRadius;
      if (w.cornerRadius !== undefined) mappedProps.borderRadius = w.cornerRadius;
      if (w.fontSize) mappedProps.fontSize = w.fontSize;
      if (w.fontWeight) mappedProps.fontWeight = w.fontWeight;
      if (w.fontFamily) mappedProps.fontFamily = w.fontFamily;
      if (w.textAlign) mappedProps.textAlign = w.textAlign;
      if (w.opacity !== undefined) mappedProps.opacity = w.opacity;
      if (w.unit) mappedProps.unit = w.unit;

      const x = (typeof w.x === 'number') ? w.x : baseX + (i % 5) * step;
      const y = (typeof w.y === 'number') ? w.y : baseY + Math.floor(i / 5) * step;
      // Support DynPage format: w -> width, h -> height
      const width = (typeof w.width === 'number') ? w.width : (typeof w.w === 'number') ? w.w : 120;
      const height = (typeof w.height === 'number') ? w.height : (typeof w.h === 'number') ? w.h : 60;
      const component = w.component || typeToComponent[type] || '';

      const normalizedWidget = { ...w, type, component, props: mappedProps, properties: mappedProps, x, y, width, height };

      normalized.push(normalizedWidget);
    } catch (error) {
      console.error(`❌ Failed to normalize widget at index ${i}:`, error, widgets[i]);
      // Skip this widget and continue with the rest
    }
  }

  return normalized;
};

const normalize = (p) => {
  try {
    if (!p || typeof p !== 'object') return p;
    const id = p.id ?? p.pageId ?? p.PageId;
    const pageName = p.pageName ?? p.PageName ?? p.name;
    const description = p.description ?? p.Description ?? '';
    const pageIcon = p.pageIcon ?? p.PageIcon ?? '';
    const isActive = (p.isActive ?? p.IsActive);
    const updatedAt = p.updatedAt ?? p.UpdatedAt ?? null;
    let widgets = p.widgets;
    let canvasSize = null;

    if (!widgets && (p.pageConfig || p.PageConfig)) {
      try {
        const cfg = JSON.parse(p.pageConfig || p.PageConfig || '{}');

        // Standard format: {"components": [...], "canvasSize": {...}}
        // Legacy fallback: {"widgets": [...]} for backward compatibility
        widgets = cfg.components || cfg.widgets || [];

        // Extract canvas size - prefer canvasSize object, fallback to root width/height
        if (cfg.canvasSize) {
          canvasSize = {
            width: cfg.canvasSize.width || 1920,
            height: cfg.canvasSize.height || 1080
          };
        } else if (cfg.width || cfg.height) {
          canvasSize = {
            width: cfg.width || 1920,
            height: cfg.height || 1080
          };
        }
      } catch (parseError) {
        console.error(`Failed to parse pageConfig for "${pageName}":`, parseError);
      }
    }

    // Check if we're using the new AxisScada components format (already normalized)
    let normalizedWidgets;
    const isAxisScadaComponentsFormat = (p.pageConfig || p.PageConfig) && JSON.parse(p.pageConfig || p.PageConfig || '{}').components;

    if (isAxisScadaComponentsFormat) {
      // Components are already in correct format, just ensure they have required fields
      normalizedWidgets = (widgets || []).map(w => ({
        ...w,
        // Ensure component field is set
        component: w.component || typeToComponent[w.type] || 'Unknown'
      }));
    } else {
      // Legacy format (DynaPages) - needs normalization
      normalizedWidgets = normalizeWidgets(widgets || []);
    }

    // Calculate canvas size from widget positions (DynPage format doesn't store canvas size)
    let calculatedCanvasWidth = 1920;
    let calculatedCanvasHeight = 1080;
    if (normalizedWidgets.length > 0) {
      let maxX = 0, maxY = 0;
      normalizedWidgets.forEach(w => {
        const rightEdge = (w.x || 0) + (w.width || 0);
        const bottomEdge = (w.y || 0) + (w.height || 0);
        if (rightEdge > maxX) maxX = rightEdge;
        if (bottomEdge > maxY) maxY = bottomEdge;
      });
      if (maxX > 0) calculatedCanvasWidth = Math.ceil(maxX + 50);
      if (maxY > 0) calculatedCanvasHeight = Math.ceil(maxY + 50);
    }

    // Extract background properties from pageConfig if available
    let backgroundColor = '#1e1e1e'; // default
    let backgroundImage = '';
    let backgroundSize = 'cover';
    let backgroundPosition = 'center';
    let backgroundRepeat = 'no-repeat';
    let backgroundOpacity = 1;

    if (p.pageConfig || p.PageConfig) {
      try {
        const cfg = JSON.parse(p.pageConfig || p.PageConfig || '{}');
        // Support both top-level and canvasProperties
        if (cfg.canvasProperties?.backgroundColor) backgroundColor = cfg.canvasProperties.backgroundColor;
        else if (cfg.backgroundColor) backgroundColor = cfg.backgroundColor;

        if (cfg.backgroundImage) backgroundImage = cfg.backgroundImage;
        if (cfg.backgroundSize) backgroundSize = cfg.backgroundSize;
        if (cfg.backgroundPosition) backgroundPosition = cfg.backgroundPosition;
        if (cfg.backgroundRepeat) backgroundRepeat = cfg.backgroundRepeat;
        if (cfg.backgroundOpacity !== undefined) backgroundOpacity = cfg.backgroundOpacity;
      } catch (e) {
        // ignore parse error
      }
    }

    return {
      id,
      pageName,
      description,
      pageIcon,
      isActive,
      updatedAt,
      companyId: p.companyId ?? p.CompanyId,  // Preserve companyId from API response
      widgets: normalizedWidgets,
      pageConfig: p.pageConfig || p.PageConfig,
      width: canvasSize?.width || calculatedCanvasWidth,
      height: canvasSize?.height || calculatedCanvasHeight,
      backgroundColor: backgroundColor,
      backgroundImage: backgroundImage,
      backgroundSize: backgroundSize,
      backgroundPosition: backgroundPosition,
      backgroundRepeat: backgroundRepeat,
      backgroundOpacity: backgroundOpacity
    };
  } catch (error) {
    console.error('❌ Error in normalize function:', error);
    throw error;
  }
};

const pageService = {
  async list() {
    try {
      // ✨ RESTful SCADA API
      const companyId = getCompanyId();
      const res = await axiosInstance.get(`/scada/pages`, {
        params: { companyId }
      });
      const data = Array.isArray(res.data) ? res.data : [];
      return data.map(normalize);
    } catch (error) {
      console.error('Failed to fetch pages from API:', error);
      return []; // Return empty array instead of throwing
    }
  },

  async get(id) {
    if (!id) return null;
    try {
      // ✨ RESTful SCADA API
      const res = await axiosInstance.get(`/scada/pages/${id}`);
      return normalize(res.data);
    } catch (error) {
      console.error(`Failed to fetch page ${id} from API:`, error);
      return null;
    }
  },

  async create(payload) {
    const companyId = getCompanyId();
    const body = ensureId({
      pageName: payload.pageName,
      description: payload.description || '',
      isActive: payload.isActive ?? true,
      widgets: payload.widgets || payload.components || [],
    });
    try {
      // ✨ RESTful SCADA API - Standard format with 'components' and 'canvasSize'
      const pageConfig = {
        components: body.widgets
      };

      // Add canvasSize if width/height provided
      if (payload.width || payload.height) {
        pageConfig.canvasSize = {
          width: payload.width || 1920,
          height: payload.height || 1080
        };
      }

      // Add background properties
      if (payload.backgroundColor) pageConfig.backgroundColor = payload.backgroundColor;
      if (payload.backgroundImage) pageConfig.backgroundImage = payload.backgroundImage;
      if (payload.backgroundSize) pageConfig.backgroundSize = payload.backgroundSize;
      if (payload.backgroundPosition) pageConfig.backgroundPosition = payload.backgroundPosition;
      if (payload.backgroundRepeat) pageConfig.backgroundRepeat = payload.backgroundRepeat;

      const res = await axiosInstance.post(`/scada/pages`, {
        companyId: companyId,
        pageName: body.pageName,
        pageIcon: payload.pageIcon || '',
        pageConfig: JSON.stringify(pageConfig),
        description: body.description,
        isActive: true
      });
      return normalize(res.data);
    } catch (error) {
      console.error('Failed to create page:', error);
      throw error;
    }
  },

  async update(id, payload) {
    try {
      // ✨ RESTful SCADA API - Standard format with 'components' and 'canvasSize'
      const pageConfig = {
        components: payload.widgets || payload.components || []
      };

      // Add canvasSize if width/height provided
      if (payload.width || payload.height) {
        pageConfig.canvasSize = {
          width: payload.width || 1920,
          height: payload.height || 1080
        };
      }

      // Add background properties
      if (payload.backgroundColor) pageConfig.backgroundColor = payload.backgroundColor;
      if (payload.backgroundImage) pageConfig.backgroundImage = payload.backgroundImage;
      if (payload.backgroundSize) pageConfig.backgroundSize = payload.backgroundSize;
      if (payload.backgroundPosition) pageConfig.backgroundPosition = payload.backgroundPosition;
      if (payload.backgroundRepeat) pageConfig.backgroundRepeat = payload.backgroundRepeat;

      const res = await axiosInstance.put(`/scada/pages/${id}`, {
        pageId: id,
        pageName: payload.pageName,
        pageIcon: payload.pageIcon || '',
        pageConfig: JSON.stringify(pageConfig),
        description: payload.description || '',
        isActive: payload.isActive ?? true
      });
      return normalize(res.data);
    } catch (error) {
      console.error(`Failed to update page ${id}:`, error);
      throw error;
    }
  },

  async remove(id) {
    try {
      // ✨ RESTful SCADA API
      await axiosInstance.delete(`/scada/pages/${id}`);
      return true;
    } catch (error) {
      console.error(`Failed to delete page ${id}:`, error);
      throw error;
    }
  },

  // Get homepage for current company
  async getHomePage() {
    try {
      const companyId = getCompanyId();
      if (!companyId) {
        console.warn('No company ID found for homepage lookup');
        return null;
      }
      const res = await axiosInstance.get(`/scada/pages/homepage/${companyId}`);
      return {
        pageId: res.data.pageId,
        pageName: res.data.pageName,
        isFallback: res.data.isFallback || false
      };
    } catch (error) {
      console.error('Failed to fetch homepage:', error);
      return null;
    }
  },

  // Set homepage for a company
  async setHomePage(pageId) {
    try {
      const companyId = getCompanyId();
      if (!companyId) {
        throw new Error('No company ID found');
      }
      const res = await axiosInstance.put(`/scada/pages/homepage/${companyId}`, {
        pageId: pageId
      });
      return res.data;
    } catch (error) {
      console.error('Failed to set homepage:', error);
      throw error;
    }
  }
};

export default pageService;
